import { ScoringFlags } from '@coverright/shared/contexts';

// ─── Re-export types used by both logic and context ──────────────────────────

export interface SectionCredits {
  profile:    number;  // 0–100, each section's own completion %
  doctors:    number;
  meds:       number;
  pharmacy:   number;
  coverage:   number;
  priorities: number;
  booking:    number;
}

/** Five tiers from the spec, plus 'none' for <18% */
export type ScoreTier = 'none' | 'basic' | 'good' | 'great' | 'excellent' | 'booked';

// ─── Weight tables ────────────────────────────────────────────────────────────

export const MATCH_WEIGHTS: Record<keyof SectionCredits, number> = {
  profile:    18,
  doctors:    28,
  meds:       24,
  pharmacy:    5,
  coverage:   10,
  priorities: 10,
  booking:     5,
};

export const PROGRESS_WEIGHTS: Record<keyof SectionCredits, number> = {
  profile:    15,
  doctors:    23,
  meds:       19,
  pharmacy:    5,
  coverage:    8,
  priorities: 10,
  booking:    20,
};

// ─── Tier thresholds ─────────────────────────────────────────────────────────

export const TIERS: [number, ScoreTier][] = [
  [100, 'booked'],
  [95,  'excellent'],
  [70,  'great'],
  [46,  'good'],
  [18,  'basic'],
  [0,   'none'],
];

// ─── Input shape (pure — no hooks, no React) ─────────────────────────────────

/** Flat primitive inputs extracted from AppState + MedicareQuote by the provider. */
export interface ScoringInput {
  // AppState fields
  situation?:        string;
  hasDoctors?:       boolean;
  hasDrugs?:         boolean;
  benefits?:         string[];
  paymentPreference?: string;
  planType?:         string;
  callScheduled?:    boolean;
  scoring?:          ScoringFlags;
  // Quote fields (counts only — no full objects)
  zip?:              string | null;
  county?:           string | null;
  doctorCount:       number;
  drugCount:         number;
  pharmacyCount:     number;
  coverageCount:     number;
}

// ─── Section-level credit calculators ────────────────────────────────────────

/**
 * Profile credit (0–100).
 * Sub-step weights: ZIP 2, DOB+gender 3, contact info 10, situation 3 = 18 total.
 * County resolving after a valid ZIP is used as proxy for DOB+contact fields
 * until the user-profile fields are wired in.
 */
export function calcProfileCredit(
  _hasZip:      boolean,
  _hasCounty:   boolean,
  _hasSituation: boolean,
): number {
  // Profile has no dedicated step in the app — always considered complete.
  return 100;
}

/**
 * Doctors / Medications credit gradient (0–100).
 *
 *   Skipped                       → 10
 *   Not started (undefined)       → 0
 *   No, unconfirmed               → 80
 *   No, reconfirmed               → 100
 *   Yes + 0 entries               → 10  (prevents dead zone)
 *   Yes + 1+ entries              → 100 (credits given immediately on first entry)
 */
export function calcGradientCredit(
  skipped:     boolean | undefined,
  hasItems:    boolean | undefined,
  reconfirmed: boolean | undefined,
  count:       number,
): number {
  if (hasItems === undefined) return skipped ? 10 : 0;
  if (!hasItems)              return reconfirmed ? 100 : 80;
  if (count >= 1)             return 100;
  return 10;
}

/**
 * Pharmacy credit (0 or 100).
 * Auto-100 when user has no drugs; otherwise requires explicit selection or an
 * already-saved pharmacy.
 */
export function calcPharmacyCredit(
  hasDrugs:         boolean | undefined,
  pharmacySelected: boolean | undefined,
  pharmaciesCount:  number,
): number {
  return hasDrugs === false || !!pharmacySelected || pharmaciesCount > 0 ? 100 : 0;
}

/**
 * Coverage credit (0 or 100).
 * 100 when the user answered the Other Coverage section or the quote already
 * contains coverage entries.
 */
export function calcCoverageCredit(
  coverageAnswered: boolean | undefined,
  coverageCount:    number,
): number {
  return !!coverageAnswered || coverageCount > 0 ? 100 : 0;
}

/**
 * Priorities credit (0 or 100).
 * 100 when any of the three preference fields is set.
 */
export function calcPrioritiesCredit(
  benefits:          string[] | undefined,
  paymentPreference: string | undefined,
  planType:          string | undefined,
): number {
  return !!(benefits?.length || paymentPreference || planType) ? 100 : 0;
}

/**
 * Booking credit (0 or 100).
 */
export function calcBookingCredit(callScheduled: boolean | undefined): number {
  return callScheduled ? 100 : 0;
}

// ─── Aggregators ──────────────────────────────────────────────────────────────

/** Compute all seven section credits from a flat ScoringInput. */
export function calcSectionCredits(input: ScoringInput): SectionCredits {
  const sf = input.scoring ?? {};

  const profile = calcProfileCredit(
    !!(input.zip && input.zip.trim().length > 0),
    !!input.county,
    !!input.situation,
  );

  const doctors = calcGradientCredit(
    sf.doctorsSkipped,
    input.hasDoctors,
    sf.doctorsReconfirmed,
    input.doctorCount,
  );

  const meds = calcGradientCredit(
    sf.drugsSkipped,
    input.hasDrugs,
    sf.drugsReconfirmed,
    input.drugCount,
  );

  const pharmacy  = calcPharmacyCredit(input.hasDrugs,    sf.pharmacySelected, input.pharmacyCount);
  const coverage  = calcCoverageCredit(sf.coverageAnswered, input.coverageCount);
  const priorities = calcPrioritiesCredit(input.benefits, input.paymentPreference, input.planType);
  const booking   = calcBookingCredit(input.callScheduled);

  return { profile, doctors, meds, pharmacy, coverage, priorities, booking };
}

/**
 * Weighted sum of section credits (0–100, rounded).
 * Pass MATCH_WEIGHTS or PROGRESS_WEIGHTS.
 */
export function calcWeightedScore(
  credits: SectionCredits,
  weights: Record<keyof SectionCredits, number>,
): number {
  return Math.round(
    (Object.keys(credits) as (keyof SectionCredits)[]).reduce(
      (sum, key) => sum + credits[key] / 100 * weights[key],
      0,
    )
  );
}

/** Map a match score to its tier. */
export function calcTier(matchScore: number): ScoreTier {
  return TIERS.find(([threshold]) => matchScore >= threshold)?.[1] ?? 'none';
}

/** Compute all derived scores from section credits. */
export function calcDerivedScores(credits: SectionCredits): {
  matchScore:    number;
  progressScore: number;
  tier:          ScoreTier;
  aiGateOpen:    boolean;
} {
  const matchScore    = calcWeightedScore(credits, MATCH_WEIGHTS);
  const progressScore = calcWeightedScore(credits, PROGRESS_WEIGHTS);
  return {
    matchScore,
    progressScore,
    tier:       calcTier(matchScore),
    aiGateOpen: matchScore >= 95,
  };
}
